/**
 * "I am a trustee of this trust" — the claim form.
 *
 * Beno's flowchart, 2026-08-18: a trustee types the trust's registration number
 * and the system checks whether that trust is already on Elreign. He was
 * explicit it should be TYPED rather than uploaded — every trustee knows the
 * number, and names get misspelled.
 *
 * ── Why this screen can never say "found it" ─────────────────────────────────
 * The endpoint answers identically whether the trust exists or not, because SA
 * trust numbers are guessable and a truthful answer would turn this box into a
 * directory of every trust in the country. So the success state shows the
 * server's own sentence and nothing else.
 *
 * That makes the copy the important part of this component. A person who has
 * just typed their trust's number and pressed a button expects to be told they
 * found it, and we cannot tell them. The screen therefore explains WHY it is
 * being vague — "we do not confirm whether a trust is on Elreign, because that
 * would let anyone check" — rather than leaving them wondering whether it
 * worked. Honest and explained beats vague and unexplained.
 */

import { useState } from 'react';
import { Modal } from '../ui/modal';
import { useClaimTrust, type ClaimedTier } from '../../lib/api/trust-access';
import { mapError } from '../../lib/error-messages';

export interface ClaimTrustDialogProps {
  open: boolean;
  onClose: () => void;
  /** Prefilled when the claim starts from a "your name is on this trust" row —
   *  the person should not have to retype a number we already know. */
  initialRegistration?: string;
  initialTier?: ClaimedTier;
  /** Prefilled from the profile so the stated name matches what the deed check
   *  will be run against. Still editable: the deed may carry a fuller name. */
  initialFullName?: string;
}

export function ClaimTrustDialog({
  open,
  onClose,
  initialRegistration = '',
  initialTier = 'trustee',
  initialFullName = '',
}: ClaimTrustDialogProps) {
  const [registration, setRegistration] = useState(initialRegistration);
  const [fullName, setFullName] = useState(initialFullName);
  const [tier, setTier] = useState<ClaimedTier>(initialTier);
  const claim = useClaimTrust();

  const canSubmit =
    registration.trim().length > 0 && fullName.trim().length >= 2 && !claim.isPending;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    claim.mutate({ registration: registration.trim(), fullName: fullName.trim(), claimedTier: tier });
  }

  function close() {
    claim.reset();
    onClose();
  }

  return (
    <Modal open={open} onClose={close} title="Ask for access to a trust" size="md">
      {claim.isSuccess ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* The server's own sentence, verbatim. Rewriting it into "request
              sent" would assert the trust exists. */}
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6 }}>{claim.data.message}</p>
          <div
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '12px 14px',
              fontSize: 13,
              lineHeight: 1.6,
              color: 'var(--ink-2)',
            }}
          >
            We do not confirm whether a trust is on Elreign, because that would let
            anyone check. If it is here, whoever administers it decides. You will get
            an email either way.
          </div>
          <button type="button" className="btn-primary" onClick={close}>
            Done
          </button>
        </div>
      ) : (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--ink-2)' }}>
            If the trust is already on Elreign, the firm or person who administers it
            will be asked to confirm you before you get access. Nothing is shared with
            you until they do.
          </p>

          <label className="form-field">
            <span>Trust registration number</span>
            <input
              type="text"
              value={registration}
              onChange={(e) => setRegistration(e.target.value)}
              placeholder="IT 610/2025(C)"
              autoComplete="off"
              required
              disabled={claim.isPending}
            />
            <small>
              As it appears on the letters of authority. Spacing and punctuation do not
              matter.
            </small>
          </label>

          <label className="form-field">
            <span>Your full name</span>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="As it appears on the trust deed"
              autoComplete="name"
              required
              minLength={2}
              disabled={claim.isPending}
            />
            <small>
              We check this against the names in the deed and show the result to whoever
              approves you.
            </small>
          </label>

          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              What are you on this trust?
            </legend>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['trustee', 'beneficiary'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTier(t)}
                  aria-pressed={tier === t}
                  disabled={claim.isPending}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 999,
                    fontSize: 13,
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                    // Matches the ChoiceCard treatment the onboarding welcome
                    // screen uses; there is no shared "selected chip" class and
                    // inventing one for two toggles is not worth the surface.
                    border: tier === t ? '1.5px solid var(--accent-600)' : '1px solid var(--hairline-2)',
                    background: tier === t ? 'var(--accent-50)' : 'var(--color-neutral-surface, #FFFFFF)',
                    color: 'var(--ink)',
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
            <small style={{ display: 'block', marginTop: 8, color: 'var(--ink-3)' }}>
              A hint for whoever approves you. They decide what you actually get.
            </small>
          </fieldset>

          {claim.isError && (
            <p role="alert" style={{ margin: 0, color: 'var(--negative)', fontSize: 14 }}>
              {mapError(claim.error)}
            </p>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn-ghost" onClick={close} disabled={claim.isPending}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={!canSubmit}>
              {claim.isPending ? 'Sending…' : 'Ask for access'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
